import { CommonModule, formatDate } from '@angular/common';
import { Component, computed, inject, input, LOCALE_ID, signal } from '@angular/core';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import DataTable from 'datatables.net-dt';
import { DatatableComponent } from 'src/app/core/components/datatable/datatable.component';
import { MetricCardComponent } from 'src/app/core/components/metric-card';
import { AverageCoverageResponse } from 'src/app/core/models/analytics/vss-models';
import { formatDateRangeLabel } from 'src/app/core/shared/common/date-range.util';
import { environment } from 'src/environments/environment';

/** Plain text cells: DataTables writes raw HTML, and render.text() is its own escaper. */
const text = DataTable.render.text();

@Component({
  selector: 'app-vss-coverage-card',
  standalone: true,
  imports: [CommonModule, MetricCardComponent, DialogsModule, DatatableComponent],
  templateUrl: './vss-coverage-card.component.html',
  styleUrl: './vss-coverage-card.component.scss',
})
export class VssCoverageCardComponent {
  private locale = inject(LOCALE_ID);

  averageCoverageResponse = input<AverageCoverageResponse>(new AverageCoverageResponse());

  public organizationId = input<number | null>();
  public dateRange = input<string[] | null>(null);
  public startDate = input<string | null>();
  public endDate = input<string | null>();

  public showPopup = signal<boolean>(false);

  public modalTitle = computed((): string => {
    const start = this.startDate();
    const end = this.endDate();
    const label = start && end ? formatDateRangeLabel([start, end]) : '';
    return label ? `Average Coverage (${label})` : 'Average Coverage';
  });

  public datatableUrl = computed((): string => {
    const id = this.organizationId();
    return id != null ? `${environment.apiUrl}/v1/vicarius/analytics/averagecoveragedetails(${id})` : '';
  });

  /** The same period the card was computed for. */
  public periodParams = computed((): Record<string, string> => {
    const start = this.startDate();
    const end = this.endDate();
    return start && end
      ? { current_start: encodeURIComponent(start), current_end: encodeURIComponent(end) }
      : {};
  });

  private date = (value: string) => (value ? formatDate(value, 'MMM d, y, h:mm:ss a', this.locale) : '');

  public datatableColumns = [
    { data: 'endpoint_name', name: 'endpoint_name', title: 'Endpoint | Device Name', type: 'string', render: text },
    { data: 'agent_id', name: 'agent_id', title: 'Endpoint ID', type: 'string', render: text },
    { data: 'device_type', name: 'device_type', title: 'Endpoint | Device Type', type: 'string', render: text },
    { data: 'sweeps', name: 'sweeps', title: 'Sweeps', type: 'num', render: text },
    { data: 'alive_sweeps', name: 'alive_sweeps', title: 'Alive Sweeps', type: 'num', render: text },
    {
      data: 'coverage_percentage',
      name: 'coverage_percentage',
      title: 'Coverage',
      type: 'num',
      render: (value: number) => (value != null ? `${value.toFixed(2)} %` : ''),
    },
    {
      data: 'is_alive',
      name: 'is_alive',
      title: 'Alive (Latest Sweep)',
      type: 'string',
      render: (value: unknown) => (value ? 'Yes' : 'No'),
    },
    { data: 'first_seen', name: 'first_seen', title: 'First Sweep', type: 'string', render: this.date },
    { data: 'last_seen', name: 'last_seen', title: 'Last Sweep', type: 'string', render: this.date },
    {
      data: 'endpoint_updated_at',
      name: 'endpoint_updated_at',
      title: 'Endpoint Updated At',
      type: 'string',
      render: this.date,
    },
  ];

  onViewData(): void {
    this.showPopup.set(true);
  }

  close(): void {
    this.showPopup.set(false);
  }
}
